import { useState, useEffect } from 'react';
import { X, Save, FileText } from 'lucide-react';

interface EnvVarEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string) => Promise<void>;
  initialContent?: string;
}

export default function EnvVarEditor({
  isOpen,
  onClose,
  onSave,
  initialContent = '',
}: EnvVarEditorProps) {
  const [content, setContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setContent(initialContent);
    }
  }, [isOpen, initialContent]);

  const handleSave = async () => {
    if (!content.trim() || isSaving) return;

    try {
      setIsSaving(true);
      await onSave(content);
    } catch (error) {
      // Error is handled by the hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const exampleContent = `# Environment Variables
DATABASE_URL=postgresql://user:password@localhost:5432/myapp
JWT_SECRET=your-super-secret-jwt-key-here
REDIS_URL=redis://localhost:6379
NODE_ENV=development
PORT=3000
API_KEY="key with spaces"`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Environment Variables Editor
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Editor */}
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Paste your .env content
              </h4>
              <p className="text-sm text-gray-600">
                Paste your environment variables in KEY=VALUE format. Comments and empty lines are supported.
              </p>
            </div>
            <div className="flex-1 p-4">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={exampleContent}
                className="w-full h-full p-4 bg-gray-800 border border-gray-600 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-100 placeholder-gray-400"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l border-gray-200 bg-gray-50">
            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Format Guidelines
              </h4>
              <div className="space-y-3 text-sm text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">Basic Format:</p>
                  <code className="block mt-1 text-xs bg-white p-2 rounded border">
                    KEY=value
                  </code>
                </div>
                
                <div>
                  <p className="font-medium text-gray-700">With Quotes:</p>
                  <code className="block mt-1 text-xs bg-white p-2 rounded border">
                    API_KEY="secret key"
                  </code>
                </div>
                
                <div>
                  <p className="font-medium text-gray-700">Comments:</p>
                  <code className="block mt-1 text-xs bg-white p-2 rounded border">
                    # Database configuration<br/>
                    DB_HOST=localhost
                  </code>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    💡 <strong>Tip:</strong> Existing variables will be replaced. 
                    This operation will update all environment variables for this project.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!content.trim() || isSaving}
            className="btn-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Variables'}
          </button>
        </div>
      </div>
    </div>
  );
}